import styled from "styled-components";
import { useTable, useSortBy, Column } from "react-table";
import colors from "core/resources/theme/colors";

export function TableComponent({
  columns,
  data,
  onClickRow,
  styles,
}: {
  styles?: object[] | any;
  columns: Column[];
  data: any[];
  onClickRow?: (index: number) => void;
}) {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data,
      },
      useSortBy
    );

  return (
    <>
      <Table {...getTableProps()} styles={styles}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column: any) => {
                return (
                  // Add the sorting props to control sorting. For this example
                  // we can add them into the header props
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    style={{ width: column.width ?? undefined }}
                  >
                    {column.render("Header")}
                    {/* Add a sort direction indicator */}
                    <span>
                      {column.isSorted
                        ? column.isSortedDesc
                          ? " 🔽"
                          : " 🔼"
                        : ""}
                    </span>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row, i) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => {
                  const cellProps = { ...cell.getCellProps() };

                  const col: any = cell.column;

                  let onClick: any = undefined;

                  if (!col.disableClick) {
                    if (typeof col.onClick === "function") {
                      onClick = col.onClick;
                    } else if (typeof onClickRow === "function") {
                      onClick = onClickRow;
                    }
                  }

                  const isClicked = typeof onClick === "function";
                  return (
                    <td
                      onClick={() => {
                        if (isClicked) {
                          onClick(i);
                        }
                      }}
                      style={{
                        width: cell.column.width,
                        maxWidth: styles?.maxWidth
                          ? `${styles?.maxWidth}px`
                          : "",
                        cursor: isClicked ? "pointer" : "default",
                      }}
                      {...cellProps}
                    >
                      {cell.render("Cell")}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </>
  );
}

interface TableProps {
  styles?: any;
}

const Table = styled.table<TableProps>`
  border-spacing: 0;
  width: 100%;
  font-size: 1.2em;

  tr {
    border-radius: 8px;
    :last-child {
      td {
        border-bottom: 0;
      }
    }
  }

  tbody > tr:nth-child(odd) {
    background-color: ${colors.black100};
  }

  thead::after {
    content: "";
    display: block;
    height: 1em;
    width: 100%;
    background: white;
  }

  th {
    color: ${(props) =>
      !props?.styles?.color ? colors.lightGray : `${props.styles.color}`};
    font-weight: ${(props) =>
      !props?.styles?.fontWeight ? 400 : props?.styles.fontWeight};
    font-size: 0.9em;
    text-align: ${(props) =>
      !props?.styles?.textAlign ? "center" : `${props?.styles.textAlign}`};
  }

  th,
  td {
    padding: 14px 24px;
    border-bottom: 1px ${colors.black100} solid;
    :last-child {
      border-right: 0;
      text-align: end;
    }
  }

  td {
    padding-top: 10px !important;
    padding-bottom: 10px !important;
    font-size: 0.9em;
    max-width: ${(props) =>
      props.styles?.maxWidth ? `${props.styles?.maxWidth}` : "unset"};
  }
`;
